import React from 'react';
// * Get the history hook from react-router-dom
import { useHistory } from 'react-router-dom/esm/react-router-dom.js';

// * Import our global singleton history as a fallback
import { history as globalHistory } from '../src/components/utils/router/history';

// Re-export the rest of the real ESM bundle
export * from 'react-router-dom/esm/react-router-dom.js';

/**
 * A Link component that prioritizes context-aware navigation,
 * falls back to a global history object, and finally allows a full
 * page reload to prevent application crashes. This was created because we
 * were having invariant errors when using the Link component from
 * react-router-dom.
 */
export function Link({ to, children, onClick, target, ...props }) {
  // Get the history object from React's context.
  // This will be `undefined` if the Link is outside a <Router>.
  const contextualHistory = useHistory();

  const handleClick = (e) => {
    // * Always execute the original onClick prop if it was provided.
    // * This pattern mirrors how event handling works throughout React and HTML.
    // * The specific, user-provided handler runs first, and then the component's more general logic follows.
    if (onClick) {
      onClick(e);
    }

    // Bail out of our custom navigation logic to let the browser handle the click.
    // This respects standard behaviors like `target="_blank"` or Cmd/Ctrl+click to open a new tab.
    // It also gives developers full control by respecting if `e.preventDefault()` was already called (on the onClick of the Link component).
    if (e.defaultPrevented || target === '_blank' || e.metaKey || e.ctrlKey) {
      return;
    }

    // * If we're still here, it's an SPA navigation. Prevent the full page reload.
    e.preventDefault();

    // * Determine which history object to use. Prioritize the one from the hook.
    const historyToUse = contextualHistory || globalHistory;

    try {
      // * If we have ANY valid history object, perform client-side navigation.
      if (historyToUse) {
        // Programmatically navigate
        historyToUse.push(to);
      }
    } catch (error) {
      // This catch block will handle any unexpected errors from history.push()
      console.error(
        'Client-side navigation failed. The browser may attempt a full page reload.',
        error
      );
    }
  };

  // Use the most likely available history object to create the href for the <a> tag
  const historyForHref = contextualHistory || globalHistory;
  const href = typeof to === 'object' ? historyForHref?.createHref(to) : to;

  return (
    <a href={href} onClick={handleClick} target={target} {...props}>
      {children}
    </a>
  );
}
